import React, { useEffect } from 'react'

import { View, StyleSheet } from 'react-native'
import delay from 'delay'

import { Title, Description, Space, HorizontalLineSpacer, Switch, Text } from '@sart/components'
import { adaptive, colors, typography } from '@sart/styles'
import client, { orderHelper, useData, useClient } from '@sart/client'
import { testID } from '@sart/utilities'

import {
  VoiceMail,
  Radio,
  Globe,
  Phone,
  AlertOctagon
} from '@sart/icons'
import { useContent} from '@sart/content'

const settingsIcons = {
  // "VMS": <VoiceMail size={40} color={colors.srGreen} />,
  // "VADSA-VAS-ROAMING": <Radio size={40} color={colors.srGreen} />,
  // "VADSA-VAS-INTERNATIONAL-CALLING": <Globe size={40} color={colors.srGreen} />,
  // "VADSA-VAS-PREMIUM-CALLING": <Phone size={40} color={colors.srGreen} />,
  "ESIMGO-ADULT-CONTENT": <AlertOctagon size={40} color={colors.brightOrange} />
}

function isSettingSelected(setting,settingInstances){
  const selectedSettingInstance = settingInstances.find((settingInstance) => settingInstance.product.productLegacyId === setting.productLegacyId)
  if (selectedSettingInstance === undefined){
    return false
  }

  const additionalInfo = selectedSettingInstance.additionalInformation !== undefined ?
  JSON.parse(selectedSettingInstance.additionalInformation) : 
  undefined

  if (additionalInfo !== undefined && additionalInfo.selected !== undefined 
    && additionalInfo.selected === "false") {
    return false
  }
  //we've gotten this far, meaning we have an instance either without additional
  //info, or the additional info does not indicate false. Either way, return true
  return true
}

export default function ServiceSettings(props) {
  // const { navigation } = props
  const content = useContent()

  const [{ settings, loading, error }, setData] = useData({ loading: true})

  const {
        cache: {
          accounts,
          vasOptions,
          customer
        } = {}
      } = useClient()

      const customerOver18 = (() => {
        if (!customer?.dob) return false;
        const dob = new Date(customer.dob);
        const now = new Date();
        const age = now.getFullYear() - dob.getFullYear();
        const m = now.getMonth() - dob.getMonth();
        const d = now.getDate() - dob.getDate();
        if (m < 0 || (m === 0 && d < 0)) {
          return age - 1 >= 18;
        }
        return age >= 18;
      })();

      useEffect(() => void fetchSettings(),[])
  // console.log('vasOptions is: ');
  // console.log(vasOptions);
  async function fetchSettings() {
    setData({ loading: true })
    try {
      const availableSettings = vasOptions.map((setting) => ({
        ...setting,
        id: setting.productId,
        label : setting.productName,
        icon: settingsIcons[setting.productLegacyId],
        value: false
      }))
      setData({settings: availableSettings})
      
      const _settingInstances = await client.callApi('service','getServiceBooleanValueAddedServiceProductInstances',[
        client.cache.accountId,
        client.cache.serviceId
      ])

      const selectedSettings = vasOptions.map((setting) => ({
        ...setting,
        id: setting.productId,
        label : setting.productName,
        icon: settingsIcons[setting.productLegacyId],
        value: isSettingSelected(setting, _settingInstances)
      }))

      setData({ loading: false, settings: selectedSettings })
    } catch (error) {
      // console.log(JSON.stringify(error,null,2))
      setData({ loading: false, error })
    }
  }

  async function handleSettingChange(setting, value) {
    setData({ loading: true })

    try {

      const nextSettings = settings.map(mappedSetting => {
        if (mappedSetting.id === setting.id) {
          return {
            ...setting,
            value
          }
        }

        return mappedSetting
      })

      setData({ settings: nextSettings })

      //provision the item through the SSB based on value being true or false
      const extendedData = {selected: value}
      console.log('extendedData is: ' + JSON.stringify(extendedData,null,2))
      const order = await client.checkoutServiceProduct(setting, extendedData)

      if (order.status === "failed"){
        //order failed, throw an exception to be caught
        throw new Error(content.menu.orderFailed)
      }

      await client.loadBalancesAndInvoices(accounts[0])

      setData({ loading: false })
    } catch (error) {
      console.log('error in handleSettingChange: ' + JSON.stringify(error,null,2))

      const nextSettings = settings.map(mappedSetting => {
        if (mappedSetting.id === setting.id) {
          return {
            ...setting,
            value: !value
          }
        }

        return mappedSetting
      })

      setData({ loading: false, error, settings: nextSettings })
    }
  }
  // console.log('settings is: ');
  // console.log(JSON.stringify(settings, null, 2));

  function getLabelTranslation(setting) {
    let toReturn = setting.label
    if(setting.displayDetails){
      setting.displayDetails.map(detail => {
        if(detail.language === client.cache.language) {
          toReturn = detail.displayName
        }
      })
      }
    return toReturn
  }

  function getDescriptionTranslation(setting) {
    let toReturn = setting.description
    if(setting.displayDetails){
      setting.displayDetails.map(detail => {
        if(detail.language === client.cache.language) {
          toReturn = detail.displayDescription
        }
      })
    }
    return toReturn
  }

  return (
    <View style={styles.wrapper}>
      <Description style={styles.description}>{content.service_settings.description}</Description>

      <Space height={24} />

      {
        settings && settings.length > 0 && settings.map((setting, index) => {
          const lastOf = index === settings.length - 1
          const label = getLabelTranslation(setting)
          const description = getDescriptionTranslation(setting)

          return (
            <React.Fragment key={setting.id}>
              <View style={styles.group}>
                {setting.icon}

                <Space width={24} />

                <View style={styles.textgroup}>
                  <Title style={styles.label}>{label}</Title>
                  <Text style={styles.info}>{description}</Text>
                </View>

                <Space width={24} />

                <Switch
                  value={setting.value}
                  disabled={loading}
                  onValueChange={(value) => handleSettingChange(setting, value)}
                  trackColor={{true: colors.brightOrange, false: colors.trivial}}
                  ios_backgroundColor={colors.trivial}          
                  thumbColor={colors.white}
                  {...testID(setting.id)}
                />
              </View>
              {loading &&
                <Text style={styles.processing}>{content.menu.voicemailStatus}</Text>
              }
              {!lastOf && <HorizontalLineSpacer />}
            </React.Fragment>
          )
        })
      }
    </View>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    paddingTop: 24,
    paddingBottom: 24,
    ...adaptive.wrapperPadding
  },
  description: {
    textAlign: 'center',
    ...typography.demi,
    color: colors.white
  },
  group: {
    flexDirection: 'row',
    // justifyContent: 'stretch',
    alignItems: 'center'
  },
  textgroup: {
    flex: 1,
    alignItems: 'flex-start'
  },
  label: {
    fontSize: 16,
    ...typography.lightbold,
    color: colors.white
  },
  info: {
    fontSize: 14
  },
  processing: {
    alignSelf: 'flex-end'
  }
})

